import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

interface ControlBarProps {
  isVideoOn: boolean;
  isAudioOn: boolean;
  isScreenSharing: boolean;
  isRecording: boolean;
  isHandRaised: boolean;
  currentUserRole: 'admin' | 'teacher' | 'student' | 'parent';
  screenShareAvailable?: boolean;
  recordingAvailable?: boolean;
  settingsAvailable?: boolean;
  onToggleVideo: () => void;
  onToggleAudio: () => void;
  onToggleScreenShare: () => void;
  onToggleRecording: () => void;
  onToggleHand: () => void;
  onLeaveClass: () => void;
  onOpenChat: () => void;
  onOpenParticipants: () => void;
  onOpenSettings: () => void;
}

const ControlBar = ({
  isVideoOn,
  isAudioOn,
  isScreenSharing,
  isRecording,
  isHandRaised,
  currentUserRole,
  screenShareAvailable = true,
  recordingAvailable = true,
  settingsAvailable = true,
  onToggleVideo,
  onToggleAudio,
  onToggleScreenShare,
  onToggleRecording,
  onToggleHand,
  onLeaveClass,
  onOpenChat,
  onOpenParticipants,
  onOpenSettings,
}: ControlBarProps) => {
  const canModerate = currentUserRole === 'teacher' || currentUserRole === 'admin';
  const canRaiseHand = currentUserRole === 'student' || currentUserRole === 'parent';

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border shadow-lg z-[960] lg:left-64">
      <div className="flex flex-col gap-2 px-3 py-2 lg:px-6 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:flex-row sm:items-center sm:justify-between sm:px-4 sm:py-3">
        <div className="flex items-center justify-center gap-2 sm:justify-start">
          <button
            onClick={onToggleAudio}
            className={`p-2.5 sm:p-3 rounded-full transition-all duration-150 ${
              isAudioOn ? 'bg-muted hover:bg-muted/80' : 'bg-error hover:bg-error/90'
            }`}
            aria-label={isAudioOn ? 'Dezactivează microfonul' : 'Activează microfonul'}
          >
            <Icon name={isAudioOn ? 'Mic' : 'MicOff'} size={20} color={isAudioOn ? 'currentColor' : 'white'} />
          </button>

          <button
            onClick={onToggleVideo}
            className={`p-2.5 sm:p-3 rounded-full transition-all duration-150 ${
              isVideoOn ? 'bg-muted hover:bg-muted/80' : 'bg-error hover:bg-error/90'
            }`}
            aria-label={isVideoOn ? 'Oprește camera' : 'Pornește camera'}
          >
            <Icon name={isVideoOn ? 'Video' : 'VideoOff'} size={20} color={isVideoOn ? 'currentColor' : 'white'} />
          </button>

          {canModerate && screenShareAvailable ? (
            <button
              onClick={onToggleScreenShare}
              className={`p-2.5 sm:p-3 rounded-full transition-all duration-150 ${
                isScreenSharing ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/80'
              }`}
              aria-label={isScreenSharing ? 'Oprește partajarea ecranului' : 'Pornește partajarea ecranului'}
            >
              <Icon name="Monitor" size={20} />
            </button>
          ) : null}

          {canModerate && recordingAvailable ? (
            <button
              onClick={onToggleRecording}
              className={`p-2.5 sm:p-3 rounded-full transition-all duration-150 ${
                isRecording ? 'bg-error text-white animate-pulse' : 'bg-muted hover:bg-muted/80'
              }`}
              aria-label={isRecording ? 'Oprește înregistrarea' : 'Pornește înregistrarea'}
            >
              <Icon name="Circle" size={20} />
            </button>
          ) : null}

          {canRaiseHand ? (
            <button
              onClick={onToggleHand}
              className={`p-2.5 sm:p-3 rounded-full transition-all duration-150 ${
                isHandRaised ? 'bg-warning text-warning-foreground' : 'bg-muted hover:bg-muted/80'
              }`}
              aria-label={isHandRaised ? 'Coboară mâna' : 'Ridică mâna'}
            >
              <Icon name="Hand" size={20} />
            </button>
          ) : null}
        </div>

        <div className="flex items-center justify-center gap-2 sm:justify-end">
          <button
            onClick={onOpenChat}
            className="p-2.5 sm:p-3 rounded-full bg-muted hover:bg-muted/80 transition-all duration-150"
            aria-label="Deschide chatul"
          >
            <Icon name="MessageSquare" size={20} />
          </button>

          <button
            onClick={onOpenParticipants}
            className="p-2.5 sm:p-3 rounded-full bg-muted hover:bg-muted/80 transition-all duration-150"
            aria-label="Vezi participanții"
          >
            <Icon name="Users" size={20} />
          </button>

          {settingsAvailable ? (
            <button
              onClick={onOpenSettings}
              className="p-2.5 sm:p-3 rounded-full bg-muted hover:bg-muted/80 transition-all duration-150"
              aria-label="Setări"
            >
              <Icon name="Settings" size={20} />
            </button>
          ) : null}

          <Button
            variant="destructive"
            size="icon"
            onClick={onLeaveClass}
            iconName="PhoneOff"
            className="ml-2 flex-shrink-0"
            aria-label="Ieși din sesiune"
          />
        </div>
      </div>
    </div>
  );
};

export default ControlBar;
